import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BookOpen, Clapperboard, Image, Library, Lightbulb, Menu, PenLine, Sparkles, Video, Wand2 } from "lucide-react";

const navItems = [
  { to: "/luo-promptti", label: "Luo prompti", desktopLabel: "Luo prompti", icon: Wand2 },
  { to: "/ideasta-promptiksi", label: "Ideasta promptiksi", desktopLabel: "Ideasta", icon: Lightbulb },
  { to: "/lyhytvideo", label: "Lyhytvideo", desktopLabel: "Lyhytvideo", icon: Clapperboard },
  { to: "/pohjat", label: "Promptipohjat", desktopLabel: "Pohjat", icon: BookOpen },
  { to: "/paranna", label: "Promptin parantaja", desktopLabel: "Parantaja", icon: Sparkles },
  { to: "/pohjat/ai-kuvapromptit-master", label: "AI-kuvaprompti", desktopLabel: "Kuva", icon: Image },
  { to: "/pohjat/ai-videopromptit-master", label: "AI-videoprompti", desktopLabel: "Video", icon: Video },
  { to: "/kirjasto", label: "Promptikirjasto", desktopLabel: "Kirjasto", icon: Library },
];

const mobileNavItems = [
  { to: "/luo-promptti", label: "Luo prompti", icon: Wand2 },
  { to: "/ideasta-promptiksi", label: "Ideasta promptiksi", icon: Lightbulb },
  { to: "/pohjat", label: "Promptipohjat", icon: BookOpen },
  { to: "/pohjat/ai-kuvapromptit-master", label: "AI-kuvaprompti", icon: Image },
  { to: "/pohjat/ai-videopromptit-master", label: "AI-videoprompti", icon: Video },
  { to: "/kirjasto", label: "Promptikirjasto", icon: Library },
];

const MOBILE_BREAKPOINT = 640;

export const AppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    closeMobileMenu();
  }, [closeMobileMenu, location.pathname]);

  useEffect(() => {
    const syncMobileBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (!badge) return;
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        badge.style.setProperty("display", "none", "important");
      } else {
        badge.style.setProperty("display", "inline-flex", "important");
      }
    };
    syncMobileBadge();
    window.addEventListener("resize", syncMobileBadge);
    const intervalId = window.setInterval(syncMobileBadge, 300);
    return () => {
      window.removeEventListener("resize", syncMobileBadge);
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2C2F2E]" data-testid="app-shell">
      <header className="sticky top-0 z-40 border-b border-[#E8E5D9]/80 bg-[#FDFCF8]/88 backdrop-blur-xl" data-testid="main-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="group flex min-w-0 items-center gap-3" data-testid="brand-home-link">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#4A6753] text-white shadow-[0_8px_30px_rgb(74,103,83,0.18)] transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-105" data-testid="brand-logo-mark">
              <PenLine className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-heading text-base font-bold sm:text-lg" data-testid="brand-name">FinnCreators</span>
              <span className="block truncate text-xs text-[#5C6360]" data-testid="brand-subtitle">Prompti Studio</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 xl:flex" data-testid="desktop-navigation">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition-colors duration-200 ${
                      isActive ? "bg-[#4A6753] text-white" : "text-[#3F4542] hover:bg-[#F5F3EC]"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {item.desktopLabel}
                </NavLink>
              );
            })}
          </nav>

          <Link
            to="/rakenneopas"
            className="hidden rounded-full border border-[#4A6753]/25 px-4 py-2 text-sm font-bold text-[#4A6753] transition-colors duration-200 hover:bg-[#4A6753] hover:text-white sm:inline-flex"
            data-testid="structure-guide-link"
          >
            Rakenneopas
          </Link>

          <div className="relative xl:hidden" data-testid="mobile-menu-wrapper">
            <button
              type="button"
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation-panel"
              onClick={() => setMobileOpen((open) => !open)}
              className="grid h-11 w-11 place-items-center rounded-full bg-[#F5F3EC] text-[#2C2F2E] transition-colors hover:bg-[#E8E5D9]"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Avaa valikko</span>
            </button>
            {mobileOpen && (
              <div id="mobile-navigation-panel" className="absolute right-0 mt-3 w-64 rounded-3xl border border-[#E8E5D9] bg-white p-3 shadow-[0_16px_48px_rgb(0,0,0,0.12)]" data-testid="mobile-menu-panel">
                {mobileNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={closeMobileMenu}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold transition-colors ${
                          isActive ? "bg-[#4A6753] text-white" : "text-[#3F4542] hover:bg-[#F5F3EC]"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </header>
      <main data-testid="main-content">{children}</main>
    </div>
  );
};